/**
 * HeldCallsTray - Multi-call MC-1 (2026-07-02)
 *
 * Renders two things:
 *   1. The `heldCalls` array from useSIP() - one chip per parked call.
 *      Clicking a chip resumes that call (auto-parks the currently active
 *      one if any).
 *   2. `parkedIncoming` - a 2nd inbound call arriving while an active
 *      call is in progress. Rendered as a banner with Accept / Reject.
 *
 * This UI is intentionally compact so it doesn't crowd the primary call
 * controls. Chips show caller number + hold duration.
 */
import React, { useEffect, useState } from 'react';


function fmtElapsed(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}


function HeldChip({ held, onResume, onHangup, onMerge, canMerge, now }) {
  const elapsed = now - (held.heldAt || Date.now());
  const info = held.callInfo || {};
  const label = info.remoteName || info.remoteNumber || info.remote || '(unknown)';
  return (
    <div className="cx-heldchip">
      <div className="cx-heldchip__body" onClick={() => onResume(held.id)} title="Resume this call">
        <div className="cx-heldchip__icon" aria-hidden="true">⏸️</div>
        <div className="cx-heldchip__meta">
          <div className="cx-heldchip__label">{label}</div>
          <div className="cx-heldchip__sub">
            {info.direction === 'inbound' ? '↙ inbound' : '↗ outbound'} · on hold {fmtElapsed(elapsed)}
          </div>
        </div>
      </div>
      {canMerge && onMerge && (
        <button
          className="cx-heldchip__merge"
          onClick={(e) => { e.stopPropagation(); onMerge(); }}
          title="Merge this call with the active call into a 3-way conference"
          aria-label="Merge into conference"
        >
          🔗 Merge
        </button>
      )}
      <button
        className="cx-heldchip__hangup"
        onClick={(e) => { e.stopPropagation(); onHangup(held.id); }}
        title="Hang up this held call"
        aria-label="Hang up held call"
      >
        ✕
      </button>
    </div>
  );
}


export default function HeldCallsTray({
  heldCalls,
  parkedIncoming,
  onResume,
  onHangupHeld,
  onAcceptParkedIncoming,
  onRejectParkedIncoming,
  onMergeHeld,
  canMergeHeld,
}) {
  // Tick every second so the "on hold X:XX" counters update.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!heldCalls?.length && !parkedIncoming) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [heldCalls?.length, !!parkedIncoming]);

  if (!heldCalls?.length && !parkedIncoming) return null;

  return (
    <div className="cx-heldtray">
      {parkedIncoming && (
        <div className="cx-heldtray__parked-incoming">
          <div className="cx-heldtray__parked-icon" aria-hidden="true">📞</div>
          <div className="cx-heldtray__parked-meta">
            <div className="cx-heldtray__parked-label">
              Incoming: {parkedIncoming.callInfo?.remoteName
                || parkedIncoming.callInfo?.remoteNumber
                || '(unknown)'}
            </div>
            <div className="cx-heldtray__parked-sub">
              Accepting will place your current call on hold.
            </div>
          </div>
          <div className="cx-heldtray__parked-actions">
            <button
              className="cx-heldtray__btn cx-heldtray__btn--accept"
              onClick={onAcceptParkedIncoming}
              title="Accept and hold current"
            >
              ✓ Accept
            </button>
            <button
              className="cx-heldtray__btn cx-heldtray__btn--reject"
              onClick={onRejectParkedIncoming}
              title="Reject 486 Busy"
            >
              ✕ Reject
            </button>
          </div>
        </div>
      )}
      {heldCalls?.length > 0 && (
        <div className="cx-heldtray__chips">
          {heldCalls.map((h) => (
            <HeldChip
              key={h.id}
              held={h}
              onResume={onResume}
              onHangup={onHangupHeld}
              onMerge={onMergeHeld}
              canMerge={canMergeHeld}
              now={now}
            />
          ))}
        </div>
      )}
    </div>
  );
}
